import { useState, useEffect } from 'react';
import axios from 'axios';
import { colorFromString } from '../utils/colors';

//const API_URL = '/api/data'; // URL relativa de tu backend
const API_URL = '/api/data';
const AUTORIZACIONES_API_URL = '/api/autorizaciones'; // New API URL
const REFRESH_INTERVAL = 300000; // 5 minutos

const useChartData = () => {
  const [data, setData] = useState([]);
  const [autorizacionesData, setAutorizacionesData] = useState([]); // New state for autorizaciones data
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  });

  const [selectedDelegacion, setSelectedDelegacion] = useState('Todas');
  const [selectedMovimiento, setSelectedMovimiento] = useState('Precargado');

  const fetchData = async () => {
    try {
      const [mainResponse, autorizacionesResponse] = await Promise.all([
        axios.get(API_URL),
        axios.get(AUTORIZACIONES_API_URL) // Fetch autorizaciones data concurrently
      ]);
      setData(mainResponse.data);
      setAutorizacionesData(autorizacionesResponse.data); // Set autorizaciones data
      setLastUpdated(new Date());
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    fetchData();
    const intervalId = setInterval(fetchData, REFRESH_INTERVAL);
    return () => clearInterval(intervalId);
  }, []);

  // Combine delegaciones from both datasets
  const allDelegaciones = [
    ...new Set(data.map(item => item.delegacion).filter(Boolean)),
    ...new Set(autorizacionesData.map(item => item.delegacion).filter(Boolean))
  ];
  const delegaciones = ['Todas', ...new Set(allDelegaciones)].sort();

  // Default movements if data is empty, otherwise extract from data
  const movimientos = ['Precargado', 'Validado']; 
  // You could also derive it: const movimientos = [...new Set(data.map(item => item.movimiento).filter(Boolean))].sort();

  const filteredData = data.filter(item => {
    const matchDelegacion = selectedDelegacion === 'Todas' || item.delegacion === selectedDelegacion;
    const matchMovimiento = item.movimiento === selectedMovimiento;
    return matchDelegacion && matchMovimiento;
  });

  const handleDateChange = (event) => {
    setSelectedDate(event.target.value);
  };

  const goToToday = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    setSelectedDate(`${year}-${month}-${day}`);
  };

  // Una tarjeta por indicador: con "Todas" las delegaciones hay una fila por delegación, se suman.
  const getIndicatorsForDate = () => {
    const byIndicador = new Map();
    filteredData
      .filter(item => item.fecha.slice(0, 10) === selectedDate)
      .forEach(item => {
        const existing = byIndicador.get(item.indicador);
        const valor = parseInt(item.valor, 10);
        if (existing) {
          existing.valor += valor;
        } else {
          byIndicador.set(item.indicador, { ...item, valor });
        }
      });
    return [...byIndicador.values()];
  };

  const getMiniChartData = (indicator) => {
    const last7Days = [...Array(7)].map((_, i) => {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }).reverse();

    const indicatorData = filteredData.filter(item => item.indicador === indicator);

    const chartData = {
      labels: last7Days,
      datasets: [
        {
          data: last7Days.map(date => {
            return indicatorData
              .filter(d => d.fecha.slice(0, 10) === date)
              .reduce((sum, d) => sum + parseInt(d.valor, 10), 0);
          }),
          borderColor: colorFromString(indicator),
          tension: 0.4,
          pointRadius: 0,
        },
      ],
    };

    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: { enabled: false },
        datalabels: { display: false }
      },
      scales: {
        x: { display: false },
        y: { display: false },
      },
    };

    return { data: chartData, options: chartOptions };
  };

  return {
    data: filteredData,
    lastUpdated,
    selectedDate,
    handleDateChange,
    goToToday,
    colorFromString,
    indicatorsForSelectedDate: getIndicatorsForDate(),
    getMiniChartData,
    delegaciones,
    selectedDelegacion,
    setSelectedDelegacion,
    movimientos,
    selectedMovimiento,
    setSelectedMovimiento,
  };
};

export default useChartData;
